// CallRow (calls round, DESIGN.md §9): one call on the strip under the top bar, or in Inbox › Calls' lists.
// Its cells sit in its CallStrip's columns (a subgrid), so several calls line up: the mark, who's calling
// (its state over the name), what we know, the line it came in on, how long, then its actions.
// - ringing: a call asking for you now. The full interrupt colour, everything on it on-interrupt, its
//   mark pulsing (--callrow-ring-time; none with prefers-reduced-motion). Only a call ringing for you,
//   or passed to you, is drawn so.
// - on, held, taken: your own line, in the interrupt's quiet wash: the call you're on (filled mark), the
//   one you answered and held (dashed: waiting), "Ben answered" for a moment.
// - quiet, other, callback, done: on the sheet. quiet: what rings that doesn't ring for you (dashed);
//   other: someone else's live call (filled); callback: a voicemail or a call to return (dashed); done:
//   a call that's over (hatched).
// - missed: a problem (nobody took it), the mark and its state in red, on the sheet: red never sits on
//   the interrupt colours.
// `settle` plays the one motion once: the row eases from the full colour to the quiet wash as you answer
// (the call's page comes in with it, CallPage's `settle`). Its actions are CallActions: one filled at
// most, the rest underlined words, or boxed controls on your own line. Reads only --callrow-*.
import { createContext, useContext, type ReactNode } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import './CallRow.css';

export type CallRowKind =
  'ringing' | 'on' | 'held' | 'taken' | 'quiet' | 'other' | 'callback' | 'done' | 'missed';

/** The mark each kind draws (DESIGN.md §2: status is drawn). */
const MARKS: Record<CallRowKind, 'ring' | 'now' | 'wait' | 'done' | 'bad'> = {
  ringing: 'ring',
  on: 'now',
  held: 'wait',
  taken: 'now',
  quiet: 'wait',
  other: 'now',
  callback: 'wait',
  done: 'done',
  missed: 'bad',
};

/** Where its rows sit: the strip under the top bar, or a list on a page (no side padding). CallStrip sets it. */
export const CallRowPlace = createContext<'strip' | 'list'>('strip');

export interface CallRowProps {
  kind: CallRowKind;
  /** Its state, small over the name ("Ringing", "On hold", "Ben answered"). */
  state: string;
  /** Who's calling ("Marguerite Osei", "New caller", "One of 3 customers"). */
  name: ReactNode;
  /** What we know: the customer, the address, what it's likely about. */
  who?: ReactNode;
  /** The line it came in on ("Main line", "Google Ads: drains"). */
  line?: string;
  /** How long it has rung or run, or when ("0:08", "10:05 a.m."). */
  time?: string;
  /** Its CallActions. */
  actions?: ReactNode;
  /** Plays the settle once, as a ring becomes your call (with CallPage's). */
  settle?: boolean;
}

export function CallRow({ kind, state, name, who, line, time, actions, settle }: CallRowProps) {
  const mark = MARKS[kind];
  const place = useContext(CallRowPlace);
  return (
    <div
      className="fl-callrow"
      data-kind={kind}
      data-place={place}
      data-settle={settle ? '' : undefined}
      role="row"
    >
      <span className="fl-callrow__markcell" role="presentation">
        <span className="fl-callrow__mark" data-mark={mark} aria-hidden="true" />
      </span>
      <span className="fl-callrow__name" role="cell">
        <span className="fl-callrow__state">{state}</span>
        <b className="fl-callrow__caller">{name}</b>
      </span>
      <span className="fl-callrow__who" role="cell">
        {who}
      </span>
      <span className="fl-callrow__line" role="cell">
        {line}
      </span>
      <span className="fl-callrow__time" role="cell">
        {time}
      </span>
      <span className="fl-callrow__acts" role="cell">
        {actions}
      </span>
    </div>
  );
}

export interface CallActionProps {
  children: ReactNode;
  /** primary: the one filled button; control: boxed (Hold, Transfer…, End call); link: underlined words. */
  variant?: 'primary' | 'control' | 'link';
  onPress?: () => void;
  isDisabled?: boolean;
  'aria-label'?: string;
  'aria-expanded'?: boolean;
  'aria-haspopup'?: 'dialog' | 'menu';
}

/** One of a CallRow's actions, drawn for the row it's on: on a ringing row, in its on-interrupt ink. */
export function CallAction({ children, variant = 'link', ...props }: CallActionProps) {
  return (
    <AriaButton className="fl-callrow__act" data-variant={variant} {...props}>
      {children}
    </AriaButton>
  );
}
